import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isPiDivOrAdmin } from '@cortex/core-types';
import { useAuth } from '../hooks/index.js';
import { useMyBugEvaluations } from '../hooks/useBugEvaluation.js';
import { useMyEvaluationSummary } from '../hooks/useEngineerEvaluation.js';
import { MemberDetailContent } from '../components/features/MemberDetailContent.js';
import { OneOnOneTimelineCard } from '../components/features/one-on-one/OneOnOneTimelineCard.js';
import { ProfileSettings } from '../components/features/ProfileSettings.js';
import { DesignerKpiTab } from '../components/features/idt-mypage/DesignerKpiTab.js';
import { DesignerSkillEvaluationTab } from '../components/features/idt-mypage/DesignerSkillEvaluationTab.js';
import { KARTE_DESIGNER_IDS } from '../components/features/idt-kpi/shared.js';
import { MyProjectsBoardTab } from '../components/features/my-page/MyProjectsBoardTab.js';
import { ProjectIntakeModal } from '../components/features/project-intake/ProjectIntakeModal.js';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { getBugIPointColor, getSkillEvalScoreColor } from '../lib/score-colors.js';
import { getTeamBadge } from '../lib/team-badge.js';
import { computePeriodRange } from '../lib/period.js';
import { useMyEvaluationTasks } from '../hooks/useEvaluationSurvey.js';
import { useMyDleScores } from '../hooks/useDleEvaluationSurvey.js';
import { DleEvaluationTab } from '../components/features/dle-mypage/DleEvaluationTab.js';
import { parseMyPageSearch, type MyPageTabId } from './-my-page-search.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business マイページ。KPI・スキル評価・担当案件起因バグ評価・個人設定をタブ切り替えで表示
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects MemberDetailContent [calls] メンバー詳細コンテンツ
 * @graph-connects OneOnOneTimelineCard [calls] 1on1 タブ（自分の週次タイムライン）
 * @graph-connects SkillEvaluationTab [calls] スキル評価タブ表示
 * @graph-connects DesignerSkillEvaluationTab [calls] IDT デザイナーのスキル評価 (個人 vs 全体デザイン評価) タブ表示
 * @graph-connects DleEvaluationTab [calls] DLE 本人の DLE 評価 (個人 vs 全体 DLE 平均) タブ表示
 * @graph-connects BugEvaluationTab [calls] バグ評価タブ表示
 * @graph-connects ProfileSettings [calls] 個人設定タブ表示
 * @graph-connects parseMyPageSearch [calls] アンケート deep link の search param 検証
 */
export const Route = createFileRoute('/my-page')({
  component: MyPage,
  validateSearch: parseMyPageSearch,
});

/**
 * PDG/ACE メンバー向けの my-page タブ定義。oneOnOne ラベルは `/members/$email` と同じ
 * `oneOnOne.tab` i18n キーを使う。
 *
 * 「DLE評価」は 1on1 の右に置く。出すのは **自分が DLE 本人 (`isDle`)** か、
 * **他人の評価を読める権限がある (`canViewOthers`: 管理者 / group_manager /
 * team_leader / PMOT 所属)** とき。どちらもサーバ側 (`GET /dle-evaluation/my-scores`) が
 * 判定して返す。`isDle` は現任 DLE 名簿 + 評価実績の OR なので、交代した元 DLE も
 * 自分の履歴を見られる。どちらでもないメンバーに出すと、中身が常に空のタブが増える。
 * @graph-connects none
 */
const getPdgAceTabs = (t: ReturnType<typeof useTranslation>['t'], showDleEval: boolean) => [
  { id: 'kpi' as const, label: 'KPI' },
  { id: 'oneOnOne' as const, label: t('oneOnOne.tab') },
  ...(showDleEval ? [{ id: 'dleEval' as const, label: t('dleEvaluation.tab') }] : []),
  { id: 'skill' as const, label: 'スキル評価' },
  { id: 'bug' as const, label: 'バグ評価' },
  { id: 'settings' as const, label: '個人設定' },
];

/**
 * 非 PI Div 社員（portalRole = 'external'、または横断 PMO = 非 pi_div × portalRole='pmo'）向け
 * my-page タブ定義。看板タブは PL/主担当 担当案件一覧を表示する。スキル評価・バグ評価・開発Pt/Release数 chart は
 * 表示せず、担当案件のみに集約する（PI Div 内部 KPI データを持たないため）。
 * @graph-connects none
 */
const EXTERNAL_TABS = [
  { id: 'board' as const, label: '看板' },
  { id: 'settings' as const, label: '個人設定' },
];

/**
 * IDT デザイナー（team pi_idt の現役メンバー）向け my-page タブ定義。
 * KPI（デザイナー版: 当月デザインpt / 四半期デザインpt表 / 担当案件一覧）とスキル評価のみ。
 * バグ評価・個人設定・看板タブ、戦闘力/開発pt/release 推移チャート、nickname/github カードは出さない。
 * @graph-connects none
 */
const IDT_DESIGNER_TABS = [
  { id: 'kpi' as const, label: 'KPI' },
  { id: 'skill' as const, label: 'スキル評価' },
];

/** @graph-connects none */
type TabId = MyPageTabId;

/**
 * スキル評価タブ。全四半期のサマリーをカード一覧で表示し詳細画面へ遷移
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全四半期のスキル評価サマリーを一覧表示し、クリックで詳細画面へ遷移
 * @graph-connects useMyEvaluationSummary [calls] 自分の全Q評価サマリー取得
 */
function SkillEvaluationTab({ email }: { email: string }) {
  const { data, isLoading } = useMyEvaluationSummary();
  const summaries = data?.summaries ?? [];

  if (isLoading) {
    return (
      <div>
        <div className="h-24 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
      </div>
    );
  }

  if (summaries.length === 0) {
    return <p className="text-[var(--color-text-muted)] text-sm">スキル評価データがありません</p>;
  }

  return (
    <div className="space-y-3">
      {summaries.map((ev) => (
        <Link
          key={ev.quarter}
          to="/engineer-evaluations/$email"
          params={{ email }}
          search={{ quarter: ev.quarter }}
        >
          <Card className="p-4 hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {ev.quarter}
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  +{String(ev.positiveCount)}
                  {ev.negativeCount > 0 && (
                    <span className="text-[var(--color-error)]"> -{String(ev.negativeCount)}</span>
                  )}
                  <span> / {String(ev.totalCriteria)} criteria</span>
                </p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${getSkillEvalScoreColor(ev.averageScore)}`}>
                  {ev.averageScore != null ? ev.averageScore.toFixed(1) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">avg score</p>
              </div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/** @graph-connects none */
const BUG_PERIOD_OPTIONS = [
  { value: '30', label: '30日' },
  { value: '90', label: '90日' },
  { value: '180', label: '180日' },
  { value: '365', label: '365日' },
  { value: 'all', label: 'すべて' },
] as const;

/**
 * バグ評価タブ。担当案件起因バグ評価一覧をテーブルで表示
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当案件起因バグ評価一覧（課題キー・種別・i-point・過失レベル・レビュー状況）を期間フィルタ付きテーブルで表示。
 * 行クリックでバグ評価詳細ページ（参照モード。編集はArchitect/Tech Lead/Adminのみ）へ遷移
 * @graph-connects useMyBugEvaluations [calls] 自分のバグ評価一覧フック
 */
function BugEvaluationTab() {
  const [period, setPeriod] = useState('all');
  const { startDate, endDate } = computePeriodRange(period);
  const { data: bugData, isLoading: bugsLoading } = useMyBugEvaluations({ startDate, endDate });
  const navigate = useNavigate();

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-[var(--color-text-muted)]">
          {bugData && <span>{bugData.total.toString()}件</span>}
        </div>
        <select
          value={period}
          onChange={(e) => {
            setPeriod(e.target.value);
          }}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm"
          aria-label="期間"
        >
          {BUG_PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* デスクトップテーブル */}
      <Card className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                課題キー
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                サマリー
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                種別
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                開発原因
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                i-point
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                過失レベル
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                レビュー
              </th>
            </tr>
          </thead>
          <tbody>
            {bugsLoading ? (
              [1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-[var(--color-border)]">
                  {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 w-16 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : bugData?.data.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-[var(--color-text-muted)]">
                  担当案件起因のバグ評価はありません
                </td>
              </tr>
            ) : (
              bugData?.data.map((item) => (
                <tr
                  key={item.issueKey}
                  onClick={() => {
                    void navigate({
                      to: '/bug-evaluation/$issueKey',
                      params: { issueKey: item.issueKey },
                    });
                  }}
                  className="cursor-pointer border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                      {item.issueKey}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate">{item.summary}</td>
                  <td className="px-4 py-3 text-xs">{item.bugType}</td>
                  <td className="px-4 py-3 text-xs max-w-[160px] truncate">{item.devCauseType}</td>
                  <td className={`px-4 py-3 text-center ${getBugIPointColor(item.iPoint)}`}>
                    {item.iPoint}
                  </td>
                  <td className="px-4 py-3 text-xs">{item.negligenceLevel}</td>
                  <td className="px-4 py-3 text-center">
                    {item.isHumanReviewed ? (
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        済
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
                        未
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* モバイルカード */}
      <div className="block md:hidden space-y-3">
        {bugsLoading ? (
          [1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
              <div className="h-4 w-3/4 mt-2 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
            </Card>
          ))
        ) : bugData?.data.length === 0 ? (
          <Card className="p-4 text-center text-[var(--color-text-muted)]">
            担当案件起因のバグ評価はありません
          </Card>
        ) : (
          bugData?.data.map((item) => (
            <Link
              key={item.issueKey}
              to="/bug-evaluation/$issueKey"
              params={{ issueKey: item.issueKey }}
            >
              <Card className="p-4 hover:bg-[var(--color-bg-hover)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                    {item.issueKey}
                  </span>
                  <span className={`text-lg ${getBugIPointColor(item.iPoint)}`}>{item.iPoint}</span>
                </div>
                <div className="text-sm truncate">{item.summary}</div>
                <div className="mt-1 text-xs text-[var(--color-text-secondary)]">
                  {item.bugType} / {item.devCauseType}
                </div>
                <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                  {item.negligenceLevel} ・ {item.isHumanReviewed ? 'レビュー済' : '未レビュー'}
                </div>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

/** @graph-connects none */
function MyPage() {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-7 w-48 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
        <div className="h-4 w-64 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
      </div>
    );
  }

  // useAuth が解決した後に内側コンポーネントをマウントすることで、
  // 初回描画から portalRole を見て正しい既定タブ
  // ('external' なら 'board'、それ以外なら 'kpi') を返せるようにする。
  return <MyPageContent user={user} />;
}

/** @graph-connects isPiDivOrAdmin [calls] PI Div所属・admin判定の共通述語 */
function MyPageContent({ user }: { user: ReturnType<typeof useAuth>['user'] }) {
  const { t } = useTranslation();
  const userEmail = user?.email ?? '';
  // IDT デザイナー（karte 対象 = KARTE_DESIGNER_IDS の nickname allowlist）は専用テンプレート。
  // teamName は yan 等で 'pi_idt' にならない個体があるため nickname allowlist で判定する。
  const idtNick = user?.nickName ? user.nickName.toLowerCase() : null;
  const isIdtDesigner =
    idtNick !== null && (KARTE_DESIGNER_IDS as readonly string[]).includes(idtNick);
  // pi_div 所属 or admin 以外（external + 横断 PMO）は「看板 + 個人設定」のみ表示。
  // PI Div 内部は KPI / 1on1 / スキル評価 / バグ評価 / 個人設定の 5 タブ（サイドバーの page-access と同基準）。
  const useBoardLayout = !isPiDivOrAdmin(user?.division, user?.portalRoles);
  // 所属チームバッジ（ac/mall/logi の team_name のみ色付き。pi_div 等は null）。
  const teamBadge = getTeamBadge(user?.teamName);
  // DLE 評価は PI Div のエンジニアだけが対象。IDT デザイナー / 事業部・横断 PMO では
  // 叩いても常に isDle=false なので enabled で抑える。
  const isPiDivEngineer = !isIdtDesigner && !useBoardLayout;
  const { data: dleScores } = useMyDleScores(isPiDivEngineer);
  // 本人が DLE でなくても、他人の評価を読める権限があればタブを出す（管理者 / team_leader /
  // PMOT）。切替で対象を選んで読む。
  const showDleEval = (dleScores?.isDle ?? false) || (dleScores?.canViewOthers ?? false);
  const tabs = isIdtDesigner
    ? IDT_DESIGNER_TABS
    : useBoardLayout
      ? EXTERNAL_TABS
      : getPdgAceTabs(t, showDleEval);
  const { surveyChild, dleSurvey, tab: tabParam } = Route.useSearch();
  const navigate = useNavigate();
  // 未回答件数は看板タブを持つユーザー（事業部 / 横断 PMO）だけが必要。
  // 看板タブが無い層で叩いても意味がないので enabled で抑える。
  const { data: surveyData } = useMyEvaluationTasks(useBoardLayout);
  const pendingSurveyCount = surveyData?.pendingCount ?? 0;
  // タブは URL の `?tab=` が持つ（直リンクを張れるように）。その人に出ないタブの ID や
  // 未指定のときは既定タブ（看板レイアウトは 'board'、それ以外は 'kpi'）に落とす。
  const defaultTab: TabId = !isIdtDesigner && useBoardLayout ? 'board' : 'kpi';
  const activeTab: TabId =
    tabParam !== undefined && tabs.some((tab) => tab.id === tabParam) ? tabParam : defaultTab;
  const [intakeOpen, setIntakeOpen] = useState(false);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">マイページ</h1>
        <p className="mt-1 text-[var(--color-text-secondary)] flex items-center gap-2 flex-wrap">
          {/* Press Start 2P (8bit) でニックネーム表示。未登録時はメールローカルパート fallback
              （フルネームは日本語で pixel フォント非対応）。-ml-1 で見出しと先頭を揃える。 */}
          <span className="text-sm -ml-1" style={{ fontFamily: "'Press Start 2P', monospace" }}>
            {user?.nickName ?? userEmail.split('@')[0] ?? userEmail}
          </span>
          {teamBadge && (
            <span
              className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: teamBadge.bg, color: teamBadge.text }}
              aria-label={`Team: ${teamBadge.label}`}
            >
              {teamBadge.label}
            </span>
          )}
          {!useBoardLayout &&
            user?.portalRoles?.map((role) => (
              <span
                key={role}
                className="inline-block rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs"
              >
                {role}
              </span>
            ))}
          {/* portalRoles と同じく、横断 PMO / external は teamName も表示しない（PI Div 所属の
              Firestore データはコンテキスト外で混乱を招くため）。 */}
          {user?.teamName && !useBoardLayout && (
            <span className="text-xs text-[var(--color-text-muted)]">{user.teamName}</span>
          )}
        </p>
      </div>

      <ProjectIntakeModal
        open={intakeOpen}
        onClose={() => {
          setIntakeOpen(false);
        }}
      />

      {/* タブ + 起票ボタン（タブと同一ライン・下線上に配置） */}
      <div className="flex items-end justify-between border-b border-[var(--color-border)]">
        <div className="flex" role="tablist" aria-label="マイページセクション">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => {
                void navigate({ to: '/my-page', search: { tab: tab.id }, replace: true });
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-[var(--color-accent-primary)] border-b-2 border-[var(--color-accent-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {tab.label}
              {/*
                未回答アンケート件数。タブを開かなくても溜まっている数が見えることが
                回答率に直結するので、看板タブのラベル横に出す。
              */}
              {tab.id === 'board' && pendingSurveyCount > 0 && (
                <span
                  className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-warning)] px-1 text-[11px] font-bold text-white"
                  aria-label={`未回答アンケート ${String(pendingSurveyCount)} 件`}
                >
                  {pendingSurveyCount}
                </span>
              )}
            </button>
          ))}
        </div>
        {/* 案件起票は所属 group / division を問わず全メンバーが利用できる。 */}
        <Button
          variant="primary"
          size="sm"
          className="mb-2 shrink-0 whitespace-nowrap rounded-full"
          onClick={() => {
            setIntakeOpen(true);
          }}
        >
          ＋案件を起票する
        </Button>
      </div>

      {/* タブコンテンツ */}
      <div role="tabpanel">
        {activeTab === 'board' && useBoardLayout && (
          <MyProjectsBoardTab
            autoOpenChildIssueKey={surveyChild}
            autoOpenDleParentIssueKey={dleSurvey}
          />
        )}
        {activeTab === 'kpi' && isIdtDesigner && user?.nickName && (
          <DesignerKpiTab nickName={user.nickName} />
        )}
        {activeTab === 'kpi' && !isIdtDesigner && !useBoardLayout && userEmail && (
          <MemberDetailContent email={userEmail} hideHeader />
        )}
        {activeTab === 'oneOnOne' && !isIdtDesigner && !useBoardLayout && userEmail && (
          <OneOnOneTimelineCard email={userEmail} showPlaceholder />
        )}
        {activeTab === 'dleEval' && showDleEval && <DleEvaluationTab />}
        {activeTab === 'skill' && isIdtDesigner && user?.nickName && (
          <DesignerSkillEvaluationTab nickName={user.nickName} />
        )}
        {activeTab === 'skill' && !isIdtDesigner && !useBoardLayout && userEmail && (
          <SkillEvaluationTab email={userEmail} />
        )}
        {activeTab === 'bug' && !isIdtDesigner && !useBoardLayout && <BugEvaluationTab />}
        {activeTab === 'settings' && !isIdtDesigner && <ProfileSettings />}
      </div>
    </div>
  );
}
